import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Menu,
  MenuItem,
  Skeleton,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import {
  debounceEventHandler,
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import moment from "moment-timezone";
import SettingsSearchField from "screens/Settings/components/SettingsSearchField";
import AddWhitelistIPModal from "screens/Settings/Security/WhitelistIPs/AddWhitelistIPModal";
import WhitelistIPsAuditModal from "screens/Settings/Security/WhitelistIPs/WhitelistIPsAuditModal";
import {
  deleteIpAllowlistEntries,
  fetchIpAllowlist,
  updateIpAllowlistEnabled,
  updateIpAllowlistEntries,
} from "screens/Settings/Security/WhitelistIPs/WhitelistIPsService";
import { IpAllowlistEntry } from "screens/Settings/Security/WhitelistIPs/WhitelistIPsTypes";
import {
  actionButtonSx,
  entryCardActionButtonSx,
  entryCardActionsSx,
  entryCardAddedBySx,
  entryCardLabelSx,
  entryCardSx,
  entryCardTextSx,
  entryCardValueSx,
  activityLinkSx,
  addedByCellSx,
  addedByTextSx,
  addedOnTextSx,
  allowlistCardSx,
  allowlistIconSx,
  allowlistNoteSx,
  allowlistTextSx,
  allowlistTitleSx,
  columnHeaderSx,
  emptyStateSx,
  labelCellSx,
  primaryButtonSx,
  rowMenuDangerItemSx,
  rowMenuItemSx,
  rowSx,
  rowToggleKnobSx,
  rowToggleTrackSx,
  searchBarSx,
  searchFieldSx,
  sectionHeaderSx,
  sectionTitleSx,
  spinnerSx,
  stateLabelSx,
  statusCellSx,
  statusTextSx,
  tableCardSx,
  tableInnerSx,
  tableScrollSx,
  toggleGroupSx,
  toggleKnobSx,
  toggleTrackSx,
  valueCellSx,
  valueDotSx,
  valueTextSx,
} from "screens/Settings/SecurityPolicies/SecurityPolicies.styles";
import securityPoliciesTokens from "screens/Settings/SecurityPolicies/SecurityPolicies.tokens";
import {
  ActivityLogIcon,
  ShieldIcon,
} from "screens/Settings/SecurityPolicies/components/SecurityPoliciesIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

const { spinner, table, rowToggle } = securityPoliciesTokens;

const COLUMNS = ["LABEL", "IP ADDRESS / RANGE", "STATUS", "ADDED BY"];

const ENFORCED_NOTE =
  "Allowlist is on — only listed IPs can access your organization.";
const OPEN_NOTE = "Allowlist is off — all traffic is currently allowed.";

// The API throws the raw `{ message, code }` body. handleErrorMessage shows
// generic copy for 400/422, so surface the server's own message for those.
const isCode = (error: any, code: number) =>
  error?.code === code || error?.status === code || error?.statusCode === code;

// Pull an IPv4/IPv6 out of a 422 lockout message so we can offer "add my IP".
const extractIp = (message?: string): string => {
  if (!message) return "";
  const v4 = message.match(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/);
  if (v4) return v4[0];
  const v6 = message.match(/\b(?:[0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}\b/);
  return v6 ? v6[0] : "";
};

/**
 * "Whitelisting IPs" block: section header with its two actions, the master
 * enforcement banner, and the searchable allowlist table — laid out exactly as
 * the Security Policies design specifies.
 */
const WhitelistIpsSection = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [entries, setEntries] = useState<IpAllowlistEntry[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApiLoading, setIsApiLoading] = useState<boolean>(false);
  const [togglingEnabled, setTogglingEnabled] = useState<boolean>(false);
  const [pendingToggleId, setPendingToggleId] = useState<string | null>(null);

  const [isDeleteModal, setIsDeleteModal] = useState<boolean>(false);
  const [selectedEntry, setSelectedEntry] = useState<IpAllowlistEntry | null>(
    null,
  );
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [editEntry, setEditEntry] = useState<IpAllowlistEntry | null>(null);
  const [prefillIp, setPrefillIp] = useState<string>("");
  const [isAuditOpen, setIsAuditOpen] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuEntry, setMenuEntry] = useState<IpAllowlistEntry | null>(null);

  // Guarded-activation (422) prompt: offer to add the admin's detected IP.
  const [lockoutIp, setLockoutIp] = useState<string>("");
  const [isLockoutModal, setIsLockoutModal] = useState<boolean>(false);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const view = await fetchIpAllowlist();
      setEnabled(!!view?.enabled);
      setEntries(view?.entries ?? []);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Frontend search over the full fetched set (no pagination — show every match).
  const search = searchText.trim().toLowerCase();
  const visibleEntries = entries.filter((entry) =>
    search
      ? entry.value.toLowerCase().includes(search) ||
        (entry.label ?? "").toLowerCase().includes(search)
      : true,
  );

  // Shared error handler: surface server message on 400/422, route 422 lockout
  // to the "add my IP" prompt, defer everything else to handleErrorMessage.
  const handleApiError = (error: any) => {
    if (isCode(error, 422)) {
      const message =
        error?.message || "This change would lock you out of the account.";
      openErrorNotification(message);
      const ip = extractIp(message);
      if (ip) {
        setLockoutIp(ip);
        setIsLockoutModal(true);
      }
      return;
    }
    if (isCode(error, 400)) {
      openErrorNotification(
        error?.message || "Please check your request and try again.",
      );
      return;
    }
    handleErrorMessage(error);
  };

  const handleToggleEnabled = async () => {
    const next = !enabled;
    setTogglingEnabled(true);
    try {
      const view = await updateIpAllowlistEnabled(next);
      setEnabled(!!view?.enabled);
      setEntries(view?.entries ?? entries);
      openSuccessNotification(
        next ? "IP allowlist enabled" : "IP allowlist disabled",
      );
    } catch (error: any) {
      handleApiError(error);
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleToggleEntry = async (entry: IpAllowlistEntry) => {
    setPendingToggleId(entry.id);
    try {
      await updateIpAllowlistEntries([
        { id: entry.id, enabled: !entry.enabled },
      ]);
      openSuccessNotification(
        !entry.enabled ? "Entry enabled" : "Entry disabled",
      );
      await fetchAllData();
    } catch (error: any) {
      handleApiError(error);
    } finally {
      setPendingToggleId(null);
    }
  };

  const deleteEntry = async (id: string) => {
    try {
      setIsApiLoading(true);
      await deleteIpAllowlistEntries([id]);
      openSuccessNotification("IP address removed from allowlist.");
      setIsDeleteModal(false);
      await fetchAllData();
    } catch (error: any) {
      handleApiError(error);
    } finally {
      setIsApiLoading(false);
    }
  };

  const handleSearchOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const openAddModal = () => {
    setEditEntry(null);
    setPrefillIp("");
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setEditEntry(null);
    setPrefillIp("");
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    entry: IpAllowlistEntry,
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuEntry(entry);
  };

  const handleMenuItemClick = (action: "edit" | "delete") => {
    const entry = menuEntry;
    setMenuAnchorEl(null);
    if (!entry) return;
    if (action === "edit") {
      setEditEntry(entry);
      setPrefillIp("");
      setIsAddModalOpen(true);
    } else {
      setSelectedEntry(entry);
      setIsDeleteModal(true);
    }
  };

  // Both small references replace the five-column grid with one card per entry.
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const getEntryCard = (entry: IpAllowlistEntry) => (
    <Box key={entry.id} sx={entryCardSx}>
      <Box sx={entryCardTextSx}>
        <Box component="span" sx={entryCardLabelSx}>
          {entry.label || "—"}
        </Box>
        <Box component="span" sx={entryCardValueSx}>
          <Box component="span" sx={valueDotSx(entry.enabled)} />
          <Box component="span" sx={valueTextSx}>
            {entry.value}
          </Box>
        </Box>
        <Box component="span" sx={entryCardAddedBySx}>
          {entry.createdBy ? `Added by ${entry.createdBy}` : ""}
          {entry.createdOn
            ? ` · ${moment(entry.createdOn).format("MMM D, YYYY")}`
            : ""}
        </Box>
      </Box>

      <Box sx={entryCardActionsSx}>
        {pendingToggleId === entry.id ? (
          <CircularProgress
            size={rowToggle.height}
            thickness={spinner.thickness}
            sx={spinnerSx}
          />
        ) : (
          <ButtonBase
            disableRipple
            role="switch"
            aria-checked={entry.enabled}
            aria-label={`Enable ${entry.value}`}
            onClick={() => handleToggleEntry(entry)}
            sx={rowToggleTrackSx(entry.enabled)}
          >
            <Box sx={rowToggleKnobSx(entry.enabled)} />
          </ButtonBase>
        )}
        <Box
          component="button"
          type="button"
          aria-label={`Actions for ${entry.value}`}
          aria-haspopup="true"
          onClick={(event: React.MouseEvent<HTMLElement>) =>
            handleOpenMenu(event, entry)
          }
          sx={entryCardActionButtonSx}
        >
          ⋮
        </Box>
      </Box>
    </Box>
  );

  const getLoadingCards = () =>
    [...Array(3)].map((_, index) => (
      <Box key={index} sx={entryCardSx} aria-hidden="true">
        <Box sx={entryCardTextSx}>
          <Skeleton variant="text" width="45%" height={16} />
          <Skeleton variant="text" width="60%" height={14} />
          <Skeleton variant="text" width="50%" height={12} />
        </Box>
      </Box>
    ));

  const getSectionHeader = () => (
    <Box sx={sectionHeaderSx}>
      <Typography component="h2" sx={sectionTitleSx}>
        Whitelisting IPs
      </Typography>
      <Button
        id="security_policies_activity_log_btn"
        variant="text"
        disableRipple
        startIcon={<ActivityLogIcon />}
        onClick={() => setIsAuditOpen(true)}
        sx={activityLinkSx}
      >
        Activity Log
      </Button>
      <Button
        id="security_policies_add_ip_btn"
        variant="contained"
        disabled={isLoading}
        onClick={openAddModal}
        sx={primaryButtonSx}
      >
        + Add Whitelist IP
      </Button>
    </Box>
  );

  const getMasterToggle = () => (
    <Box sx={allowlistCardSx}>
      <Box sx={allowlistIconSx}>
        <ShieldIcon />
      </Box>
      <Box sx={allowlistTextSx}>
        <Typography component="h3" sx={allowlistTitleSx}>
          Enforce IP allowlist
        </Typography>
        <Typography component="div" sx={allowlistNoteSx(enabled)}>
          {enabled ? ENFORCED_NOTE : OPEN_NOTE}
        </Typography>
      </Box>
      <Box sx={toggleGroupSx}>
        {togglingEnabled ? (
          <CircularProgress
            size={spinner.size}
            thickness={spinner.thickness}
            sx={spinnerSx}
          />
        ) : (
          <Typography component="span" sx={stateLabelSx}>
            {enabled ? "ON" : "OFF"}
          </Typography>
        )}
        <ButtonBase
          disableRipple
          role="switch"
          aria-checked={enabled}
          aria-label="Enforce IP allowlist"
          disabled={togglingEnabled || isLoading}
          onClick={handleToggleEnabled}
          sx={toggleTrackSx(enabled)}
        >
          <Box sx={toggleKnobSx(enabled)} />
        </ButtonBase>
      </Box>
    </Box>
  );

  const getRow = (entry: IpAllowlistEntry, index: number) => (
    <Box key={entry.id} sx={rowSx(index === visibleEntries.length - 1)}>
      <Box component="span" sx={labelCellSx} title={entry.label || undefined}>
        {entry.label || "—"}
      </Box>

      <Box component="span" sx={valueCellSx}>
        <Box component="span" sx={valueDotSx(entry.enabled)} />
        <Box component="span" sx={valueTextSx}>
          {entry.value}
        </Box>
      </Box>

      <Box component="span" sx={statusCellSx}>
        {pendingToggleId === entry.id ? (
          <CircularProgress
            size={rowToggle.height}
            thickness={spinner.thickness}
            sx={spinnerSx}
          />
        ) : (
          <ButtonBase
            disableRipple
            role="switch"
            aria-checked={entry.enabled}
            aria-label={`Enable ${entry.value}`}
            onClick={() => handleToggleEntry(entry)}
            sx={rowToggleTrackSx(entry.enabled)}
          >
            <Box sx={rowToggleKnobSx(entry.enabled)} />
          </ButtonBase>
        )}
        <Box component="span" sx={statusTextSx(entry.enabled)}>
          {entry.enabled ? "ACTIVE" : "INACTIVE"}
        </Box>
      </Box>

      <Box component="span" sx={addedByCellSx}>
        <Box component="span" sx={addedByTextSx} title={entry.createdBy}>
          {entry.createdBy || "—"}
        </Box>
        {entry.createdOn && (
          <Box component="span" sx={addedOnTextSx}>
            {moment(entry.createdOn).format("MMM D, YYYY")}
          </Box>
        )}
      </Box>

      <Box component="span">
        <Box
          component="button"
          type="button"
          aria-label={`Actions for ${entry.value}`}
          aria-haspopup="true"
          onClick={(event: React.MouseEvent<HTMLElement>) =>
            handleOpenMenu(event, entry)
          }
          sx={actionButtonSx}
        >
          ⋮
        </Box>
      </Box>
    </Box>
  );

  const getLoadingRows = () =>
    [...Array(table.skeletonRows)].map((_, index) => (
      <Box key={index} sx={rowSx(index === table.skeletonRows - 1)}>
        <Skeleton variant="text" width="60%" height={table.skeletonHeight} />
        <Skeleton variant="text" width="70%" height={table.skeletonHeight} />
        <Skeleton variant="text" width="65%" height={table.skeletonHeight} />
        <Skeleton variant="text" width="80%" height={table.skeletonHeight} />
        <Box />
      </Box>
    ));

  const getTable = () => (
    <Box sx={tableCardSx}>
      <Box sx={searchBarSx}>
        <SettingsSearchField
          id="whitelist_ips_search_box"
          placeholder="Search by IP address or label"
          width="100%"
          onChange={debounceEventHandler(
            handleSearchOnChange,
            strings.SEARCH_TIME_OUT,
          )}
          sx={searchFieldSx}
        />
      </Box>

      {isCompact ? (
        isLoading ? (
          getLoadingCards()
        ) : visibleEntries.length > 0 ? (
          visibleEntries.map(getEntryCard)
        ) : (
          <EmptyState
            icon="shield"
            accent="violet"
            title="No allowlisted IPs yet"
            body="Add an address or range and only traffic from it can reach this account."
          />
        )
      ) : (
        <Box sx={tableScrollSx}>
          <Box sx={tableInnerSx}>
            <Box sx={columnHeaderSx}>
              {COLUMNS.map((column) => (
                <Box component="span" key={column}>
                  {column}
                </Box>
              ))}
              <Box component="span" />
            </Box>

            {isLoading ? (
              getLoadingRows()
            ) : visibleEntries.length > 0 ? (
              visibleEntries.map(getRow)
            ) : (
              <EmptyState
                icon="shield"
                accent="violet"
                title="No allowlisted IPs yet"
                body="Add an address or range and only traffic from it can reach this account."
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {getSectionHeader()}
      {getMasterToggle()}
      {getTable()}

      <Menu
        id="whitelist-ip-actions-menu"
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => handleMenuItemClick("edit")}
          sx={rowMenuItemSx}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuItemClick("delete")}
          sx={rowMenuDangerItemSx}
        >
          Delete
        </MenuItem>
      </Menu>

      {isAddModalOpen && (
        <AddWhitelistIPModal
          isVisible={isAddModalOpen}
          handleDialogClose={closeAddModal}
          onSuccess={fetchAllData}
          editEntry={editEntry}
          prefillValue={prefillIp}
          prefillLabel={prefillIp ? "My current IP" : ""}
        />
      )}

      {isAuditOpen && (
        <WhitelistIPsAuditModal
          isVisible={isAuditOpen}
          handleDialogClose={() => setIsAuditOpen(false)}
        />
      )}

      {isDeleteModal && selectedEntry && (
        <AppModal
          open={isDeleteModal}
          onClose={() => setIsDeleteModal(false)}
          title="Please confirm"
          tone="destructive"
          size="sm"
          busy={isApiLoading}
          primaryLabel="Delete"
          onPrimary={() => deleteEntry(selectedEntry.id)}
          primaryLoading={isApiLoading}
          cancelLabel="Cancel"
          onCancel={() => setIsDeleteModal(false)}
        >
          <Typography id="delete-ip-message" sx={bodyTextSx}>
            {"Do you really want to remove "}
            <span style={{ fontWeight: 700 }}>{selectedEntry.value}</span>
            {" from the allowlist?"}
          </Typography>
        </AppModal>
      )}

      {isLockoutModal && (
        <AppModal
          open={isLockoutModal}
          onClose={() => setIsLockoutModal(false)}
          title="Add your current IP?"
          // A warning, not a deletion — the admin is being saved from a lockout.
          tone="neutral"
          size="sm"
          busy={isApiLoading}
          primaryLabel="Add my IP"
          onPrimary={() => {
            setIsLockoutModal(false);
            setEditEntry(null);
            setPrefillIp(lockoutIp);
            setIsAddModalOpen(true);
          }}
          cancelLabel="Cancel"
          onCancel={() => setIsLockoutModal(false)}
        >
          <Typography sx={bodyTextSx}>
            {"This change would lock you out. Add your current IP "}
            <span style={{ fontWeight: 700 }}>{lockoutIp}</span>
            {" to the allowlist first?"}
          </Typography>
        </AppModal>
      )}

      {isApiLoading && <CustomLoader isLoading={isApiLoading} />}
    </>
  );
};

export default WhitelistIpsSection;
